import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

@Injectable()
export class AppRouteReuseStrategy extends RouteReuseStrategy {

  private cache = new Map<string, DetachedRouteHandle>();
  // Paths that should be destroyed (not detached+cached) on the next navigation away.
  // Set when a tab is closed while it is the active/displayed route.
  private pendingClose = new Set<string>();

  private routeKey(route: ActivatedRouteSnapshot): string {
    return route.routeConfig?.path ?? '';
  }

  /** Called by TabService before navigating away from a tab that is being closed. */
  markClosing(basePath: string): void {
    const stripped = basePath.replace(/^\//, '');
    this.pendingClose.add(stripped);
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const key = this.routeKey(route);
    const base = key.split('/')[0];
    if (this.pendingClose.has(key) || this.pendingClose.has(base)) {
      this.pendingClose.delete(key);
      this.pendingClose.delete(base);
      return false;   // let Angular destroy the component; don't detach & cache it
    }
    // Detach any named feature route (non-empty path = admin feature page)
    return key.length > 0;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.routeKey(route);
    if (handle) {
      this.cache.set(key, handle);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.cache.has(this.routeKey(route));
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this.cache.get(this.routeKey(route)) ?? null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  /** Called by TabService when a tab is closed to free its cached component tree. */
  clearCache(basePath: string): void {
    // basePath is like '/shift-planner' or '/employee-schedule'
    // cache keys are route pattern strings like 'shift-planner' or 'employee-schedule/:id'
    const stripped = basePath.replace(/^\//, '');
    this.cache.forEach((handle, key) => {
      if (key === stripped || key.split('/')[0] === stripped) {
        this.destroyHandle(handle);
        this.cache.delete(key);
      }
    });
  }

  private destroyHandle(handle: DetachedRouteHandle): void {
    const ref = (handle as any)?.componentRef;
    if (ref) ref.destroy();
  }
}
